


// Removed top-level fetchAuthSession() call that was causing AuthUserPoolException

import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Alert,
  Card,
  CardContent,
  Chip,
  Snackbar
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AWARD_CATEGORIES, getAwardCategoryLabel } from '../src/config/2025/appConfig';
import { authService, USER_GROUPS } from '../src/services/authService';
import { endorsementService } from '../src/services/endorsementService';
import { fetchAuthSession } from 'aws-amplify/auth';
// Helper function to get incubation programme display text
const getIncubationProgramDisplay = (code: string): string => {
  switch (code) {
    case 'N': return 'Not Applicable 不適用';
    case 'C': return 'Cyberport 數碼港';
    case 'H': return 'Hong Kong Science & Technology Parks Corporation (HKSTP) 香港科技園公司';
    case 'O': return 'Others 其他';
    default: return code || 'N/A';
  }
};

// Helper function to get judge name from Cognito attributes
const getJudgeName = async (email: string): Promise<string> => {
  try {
    // This would typically fetch from Cognito or a backend API
    // For now, we'll use a simple mapping or return the email
    // console.log(`Getting judge name for email: ${email}`);

    // In a real implementation, this would call:
    // const user = await cognitoUserService.getUserByEmail(email);
    // return user.fullName || user.name || email;

    // For now, return a formatted version of the email
    return email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  } catch (error) {
    console.error('Error getting judge name:', error);
    return email;
  }
};
import { applicationService } from '../src/services/applicationService';
import { getUrl } from 'aws-amplify/storage';
import { getAllFiles, getFileType, getOriginalFilename } from '../src/utils/fileUtils';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';

interface ApplicationDetail {
  companyNameEng: string;
  companyNameChi?: string;
  entryTitleEng: string;
  entryTitleChi?: string;
  entryDescription: string;
  companyDescription: string;
  businessRegNo: string;
  incorporationNo: string;
  incorporationDate: string;
  companyAddress: string;
  companyCEO?: string;
  companyBusiness: string;
  companyURL: string;
  isStartup: string;
  employeeCount?: string;
  incubationProgram: string;
  incubationDescription?: string;
  entryPhotoUrl?: string;
  supportingDocumentUrl?: string;
  logoAIUrl?: string;
  logoJPEGUrl?: string;
  brandGuidelineUrl?: string;
  primaryContactName: string;
  primaryContactTitle: string;
  primaryContactPhone: string;
  primaryContactEmail: string;
  secondaryContactName: string;
  secondaryContactTitle: string;
  secondaryContactPhone: string;
  secondaryContactEmail: string;
  awardCategory: string;
  awardName?: string;
  innovationCreativity: string;
  functionalityErgonomics: string;
  quality: string;
  benefitsImpact: string;
  marketPotential: string;
  ecologicalCompatibility: string;
  videoLink?: string;
  submissionUsage: boolean;
  applicationDeclaration: boolean;
  patentStatus: string;
  patentNumber?: string;
  patentTitle?: string;
  patentProprietor?: string;
  patentCountry?: string;
  patentDate?: string;
  patentStatusType?: string;
  intellectualProperty: string;
  disputeDescription?: string;
  eventAdminCost: boolean;
  verifyCode: string;
}

interface Endorsement {
  id?: string;
  judgeEmail: string;
  judgeName?: string;
  mark: number;
  comment?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface Application {
  id?: string;
  applicationId?: string;
  applicationYear?: string;
  applicationDetail: ApplicationDetail;
  applicationStatus: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
  // Endorsement data will be stored in separate Endorsement table
}

const Applications: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState<Application[]>([]);
  const [filteredCategory, setFilteredCategory] = useState<string>('ALL');
  const [search, setSearch] = useState('');
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // User and permission state
  const [currentUser, setCurrentUser] = useState<{ id: string; email: string; groups: string[]; fullName?: string; isAuthenticated: boolean; assignedCategories?: string[] } | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [filePreviewOpen, setFilePreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string; type: string } | null>(null);

  // Endorsement modal state
  const [endorsementModalOpen, setEndorsementModalOpen] = useState(false);
  const [selectedAppForEndorsement, setSelectedAppForEndorsement] = useState<Application | null>(null);
  const [endorsementForm, setEndorsementForm] = useState({
    mark: '',
    comment: ''
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Submitted information modal state
  const [submittedInfoModalOpen, setSubmittedInfoModalOpen] = useState(false);
  const [selectedSubmittedEndorsement, setSelectedSubmittedEndorsement] = useState<Endorsement | null>(null);

  // Endorsements state - Map of applicationId to user's endorsement details
  const [userEndorsements, setUserEndorsements] = useState<Map<string, Endorsement>>(new Map());
  // Quick lookup for current user's endorsed applications
  const [userEndorsedAppIds, setUserEndorsedAppIds] = useState<Set<string>>(new Set());
  // All endorsements for admin panel (loaded on demand)
  const [adminEndorsements, setAdminEndorsements] = useState<Map<string, Endorsement[]>>(new Map());

  // Edit functionality state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState<Application | null>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Sorting state
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Handle column sorting
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const loadUser = useCallback(async () => {
    try {
      setUserLoading(true);

      // Fetch and log auth session
      try {
        await fetchAuthSession();
      } catch (sessionError) {
        console.error('❌ Failed to fetch auth session:', sessionError);
      }

      // Let's also check the raw Cognito user and attributes
      try {
        const { getCurrentUser: getCognitoUser, fetchUserAttributes } = await import('aws-amplify/auth');
        await getCognitoUser();
        const attributes = await fetchUserAttributes();


        // Let's check the JWT token for group information
        try {
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const authSession = await fetchAuthSession();
          const idToken = authSession.tokens?.idToken?.toString();
          if (idToken) {
            // Decode the JWT token to see if it contains group information
            const tokenParts = idToken.split('.');
            if (tokenParts.length === 3) {
              JSON.parse(atob(tokenParts[1]));
            }
          }
        } catch (tokenError) {
          console.error('❌ Error decoding JWT token:', tokenError);
        }

        // Let's also test the cognitoUserService directly
        try {
          const { cognitoUserService } = await import('../src/services/cognitoUserService');
          await cognitoUserService.getUserGroups(attributes.email || '');
        } catch (groupError) {
          console.error('❌ Error getting groups from cognitoUserService:', groupError);
        }
      } catch (debugError) {
        console.error('❌ Error fetching raw Cognito data:', debugError);
      }

      const user = await authService.getCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      console.error('Failed to load user:', error);
      setError('Failed to load user information');
    } finally {
      setUserLoading(false);
    }
  }, []);

  // Load current user's endorsements in one batch
  const loadEndorsements = useCallback(async () => {
    if (!currentUser?.email) return;

    try {
      // Fetch all endorsements by current user in one API call
      const userEndorsementsList = await endorsementService.getEndorsementsByJudge(currentUser.email);

      // Create maps for quick lookup
      const userEndorsementsMap = new Map<string, Endorsement>();
      const userEndorsedIds = new Set<string>();

      userEndorsementsList.forEach(endorsement => {
        userEndorsementsMap.set(endorsement.applicationId, endorsement);
        userEndorsedIds.add(endorsement.applicationId);
      });

      //console.log('userEndorsedIds', userEndorsedIds);
      setUserEndorsements(userEndorsementsMap);
      setUserEndorsedAppIds(userEndorsedIds);
    } catch (error) {
      console.error('Error loading endorsements:', error);
    }
  }, [currentUser?.email]);

  // Load all endorsements for a specific application (for admin panel)
  const loadAllEndorsementsForApplication = useCallback(async (applicationId: string): Promise<Endorsement[]> => {
    // Check cache first
    const cached = adminEndorsements.get(applicationId);
    if (cached) {
      return cached;
    }

    try {
      const endorsements = await endorsementService.getEndorsementsForApplication(applicationId);
      // Cache the result
      setAdminEndorsements(prev => new Map(prev.set(applicationId, endorsements)));
      return endorsements;
    } catch (error) {
      console.error('Error loading all endorsements for application:', error);
      return [];
    }
  }, [adminEndorsements]);

  useEffect(() => {
    loadUser();
    loadApplications();

    // Check for category filter in URL
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      setFilteredCategory(categoryFromUrl);
    }
  }, [searchParams, loadUser]);

  // Load endorsements when current user is available
  useEffect(() => {
    if (currentUser?.email) {
      loadEndorsements();
    }
  }, [currentUser?.email, loadEndorsements]);

  // Helper functions for permissions
  const isJudge = () => {
    const result = currentUser && authService.isInGroup(currentUser, USER_GROUPS.JUDGE);
    // console.log('isJudge() called - currentUser:', currentUser, 'result:', result);
    return result;
  };


  const canEditApplications = () => {
    return currentUser && authService.hasPermission(currentUser, 'canManageApplications');
  };

  const canEndorseApplications = () => {
    return isJudge();
  };

  // Get available categories for the current user
  const getAvailableCategories = () => {
    if (!currentUser) {
      // console.log('No current user, returning all categories');
      return AWARD_CATEGORIES;
    }

    if (isJudge()) {
      // Judges can only see their assigned categories
      const assignedCategories = currentUser.assignedCategories || [];
      // console.log('Judge assigned categories:', assignedCategories);
      const filteredCategories = AWARD_CATEGORIES.filter(category => assignedCategories.includes(category.value));
      // console.log('Filtered categories for judge:', filteredCategories);
      return filteredCategories;
    } else {
      // Admins can see all categories
      // console.log('Admin user, returning all categories');
      return AWARD_CATEGORIES;
    }
  };

      const loadApplications = async () => {
    try {
      setLoading(true);
      setError('');

      const data = await applicationService.getApplications();

      // Ensure data is always an array and handle potential issues
      if (Array.isArray(data)) {
        // Map ApplicationSubmission to Application format
        const mappedData: Application[] = data.map(app => ({
          id: app.id,
          applicationId: app.applicationId,
          applicationYear: app.applicationYear,
          applicationDetail: app.applicationDetail,
          applicationStatus: app.applicationStatus,
          status: app.status,
          createdAt: app.createdAt,
          updatedAt: app.updatedAt,
          updatedBy: app.updatedBy,
        }));
        setApplications(mappedData);
      } else {
        setApplications([]);
        setError('Invalid data format received from server');
      }
    } catch (err) {
      setError(`Failed to load applications: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setApplications([]);
    } finally {
      setLoading(false);
    }
    };

  const handleFilterChange = (event: { target: { value: string } }) => {
    const newCategory = event.target.value;
    setFilteredCategory(newCategory);

    // Update URL parameter
    if (newCategory === 'ALL') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', newCategory);
    }
    setSearchParams(searchParams);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleInfoClick = async (app: Application) => {
    setSelectedApp(app);
    // Load admin endorsements for the selected application if user is admin
    if (app.id && !isJudge()) {
      await loadAllEndorsementsForApplication(app.id);
    }
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedApp(null);
    setIsEditMode(false);
    setEditData(null);
  };

  const handleEditClick = () => {
    if (selectedApp) {
      setIsEditMode(true);
      setEditData({ ...selectedApp });
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditData(null);
  };

  const handleSaveEdit = async () => {
    if (!editData || !selectedApp?.id) return;

    setSaving(true);
    try {
      const result = await applicationService.updateApplication(selectedApp.id, editData);

      if (result.success) {
        // Update the applications list
        setApplications(prev => prev.map(app =>
          app.id === selectedApp.id ? { ...app, ...editData } : app
        ));

        // Update the selected app
        setSelectedApp({ ...selectedApp, ...editData });

        setIsEditMode(false);
        setEditData(null);

        setSnackbar({
          open: true,
          message: 'Application updated successfully!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: result.message,
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update application',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditFieldChange = (field: keyof ApplicationDetail, value: string | boolean) => {
    if (editData) {
      setEditData({
        ...editData,
        applicationDetail: {
          ...editData.applicationDetail,
          [field]: value
        }
      });
    }
  };

  // Endorsement handlers
  const handleEndorseClick = (app: Application) => {
    setSelectedAppForEndorsement(app);
    setEndorsementForm({ mark: '', comment: '' });
    setEndorsementModalOpen(true);
  };

  const handleEndorsementClose = () => {
    setEndorsementModalOpen(false);
    setSelectedAppForEndorsement(null);
    setEndorsementForm({ mark: '', comment: '' });
  };

  // Handle viewing submitted endorsement information
  const handleViewSubmittedInfo = (app: Application) => {
    if (app.id) {
      const userEndorsement = userEndorsements.get(app.id) || null;
      setSelectedSubmittedEndorsement(userEndorsement);
      setSubmittedInfoModalOpen(true);
    }
  };

  const handleSubmittedInfoClose = () => {
    setSubmittedInfoModalOpen(false);
    setSelectedSubmittedEndorsement(null);
  };

  const handleEndorsementSubmit = () => {
    if (!endorsementForm.mark) {
      setSnackbar({
        open: true,
        message: 'Please select a mark',
        severity: 'error'
      });
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleConfirmEndorsement = async () => {
    if (!selectedAppForEndorsement?.id || !currentUser?.email) return;

    setSaving(true);
    try {
      // Get judge name from Cognito attributes
      const judgeName = await getJudgeName(currentUser.email);

      // Check if judge has already endorsed this application
      const hasEndorsed = await endorsementService.hasJudgeEndorsed(
        selectedAppForEndorsement.id,
        currentUser.email
      );

      if (hasEndorsed) {
        // Update existing endorsement
        await endorsementService.updateEndorsement(
          selectedAppForEndorsement.id,
          currentUser.email,
          parseInt(endorsementForm.mark),
          endorsementForm.comment
        );
      } else {
        // Create new endorsement
        await endorsementService.createEndorsement(
          selectedAppForEndorsement.id,
          currentUser.email,
          judgeName,
          parseInt(endorsementForm.mark),
          endorsementForm.comment
        );
      }

      // Reload user's endorsements to reflect the changes
      await loadEndorsements();

      setSnackbar({
        open: true,
        message: 'Endorsement submitted successfully!',
        severity: 'success'
      });

      handleEndorsementClose();
      setConfirmDialogOpen(false);
    } catch (error) {
      console.error('Error submitting endorsement:', error);
      setSnackbar({
        open: true,
        message: 'Failed to submit endorsement',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Helper functions for file handling


  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'image': return <ImageIcon />;
      case 'document': return <DescriptionIcon />;
      default: return <DescriptionIcon />;
    }
  };

  const handleFilePreview = async (url: string, name: string) => {
    try {
      const fileType = getFileType(url);

      // Ensure user is authenticated and has valid session
      if (!currentUser || !currentUser.isAuthenticated) {
        throw new Error('User not authenticated');
      }

      // Refresh auth session before attempting preview
      try {
        await fetchAuthSession();
      } catch (authError) {
        console.warn('⚠️ Auth session refresh failed for preview, proceeding anyway:', authError);
      }

      // Get the proper preview URL from Amplify Storage
      const previewUrl = await getUrl({
        key: url,
        options: {
          validateObjectExistence: false,
          expiresIn: 3600 // 1 hour
        }
      });

      setPreviewFile({ url: previewUrl.url.toString(), name, type: fileType });
      setFilePreviewOpen(true);
            } catch (error) {
          console.error('🚫 File preview error:', error);
          alert('Failed to preview file. Please try again.');
        }
  };

                 const handleFileDownload = async (url: string, name: string) => {
    try {
      // console.log('Starting download for:', name, 'URL:', url);
      const originalFilename = getOriginalFilename(url, name);
      // console.log('Original filename:', originalFilename);

      // Ensure user is authenticated and has valid session
      if (!currentUser || !currentUser.isAuthenticated) {
        throw new Error('User not authenticated');
      }

      // Refresh auth session before attempting download
      try {
        await fetchAuthSession();
      } catch (authError) {
        console.warn('⚠️ Auth session refresh failed, proceeding with download:', authError);
      }

      // Get the proper download URL from Amplify Storage
      
      // Try multiple approaches for getting the file URL
      let downloadUrl: Awaited<ReturnType<typeof getUrl>> | null = null;
      try {
        // First try with standard configuration
        downloadUrl = await getUrl({
          key: url,
          options: {
            validateObjectExistence: false,
            expiresIn: 3600, // 1 hour
            useAccelerateEndpoint: false
          }
        });
      } catch (getUrlError) {
        console.error('❌ Standard getUrl failed:', getUrlError);
        
        // Try with different key formats if the first attempt fails
        const alternativeKeys = [
          url.startsWith('public/') ? url : `public/${url}`,
          url.startsWith('public/') ? url.replace('public/', '') : url,
          `public/applications/${url.split('/').slice(-2).join('/')}`  // Extract last two path segments
        ];
        
        let succeeded = false;
        let lastError = getUrlError;
        
        for (const altKey of alternativeKeys) {
          try {
            downloadUrl = await getUrl({
              key: altKey,
              options: {
                validateObjectExistence: false,
                expiresIn: 3600,
                useAccelerateEndpoint: false
              }
            });
            succeeded = true;
            break;
          } catch (altError) {
            console.error('❌ Alternative key format failed:', altKey, altError);
            lastError = altError;
          }
        }
        
        if (!succeeded) {
          throw lastError;
        }
      }

      if (!downloadUrl) {
        throw new Error('Failed to get download URL for file');
      }

      // console.log('Download URL obtained:', downloadUrl.url.toString());

      // Fetch the file content
      const response = await fetch(downloadUrl.url.toString());
      if (!response.ok) {
        console.error('🚫 Fetch failed with status:', response.status);
        console.error('🚫 Fetch failed with statusText:', response.statusText);
        console.error('🚫 File key that failed:', url);
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      // Get the file as a blob
      const blob = await response.blob();
      // console.log('Download completed, size:', blob.size);
      // console.log('File blob created, size:', blob.size);

      // Create a blob URL for download
      const blobUrl = URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = originalFilename;
      link.style.display = 'none'; // Hide the link
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL after a short delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);

      // console.log('Download initiated for:', originalFilename);
                    } catch (error) {
            console.error('File download error:', error);

            // Type guard to safely access error properties
            const errorObj = error as { message?: string; name?: string; code?: string; statusCode?: number };
            console.error('Error details:', {
              message: errorObj.message,
              name: errorObj.name,
              code: errorObj.code,
              statusCode: errorObj.statusCode
            });

            // More specific error messages
            if (errorObj.code === 'AccessDenied' || errorObj.statusCode === 403) {
              console.error('🚫 Access denied for file download. User groups:', currentUser?.groups);
              console.error('🚫 Failed file key/path:', url);
              console.error('🚫 Storage bucket configured for:', 'amplify-amplifyvitereactt-smartcitystoragebucket74-bvrlpcro658s');
              console.error('🚫 Allowed patterns from config:', [
                'public/applications/photos/*',
                'public/applications/documents/*', 
                'public/applications/logos/*',
                'public/applications/guidelines/*',
                'applications/photos/*',
                'applications/documents/*',
                'applications/logos/*',
                'applications/guidelines/*'
              ]);
              if (currentUser) {
                console.error('🚫 Is SUPER ADMIN:', authService.isInGroup(currentUser, USER_GROUPS.SUPER_ADMIN));
                console.error('🚫 Can view all data:', authService.hasPermission(currentUser, 'canViewAllData'));
              }
              
              // Check if the file path matches allowed patterns
              const allowedPatterns = [
                /^public\/applications\/(photos|documents|logos|guidelines)\//,
                /^applications\/(photos|documents|logos|guidelines)\//,
                /^applications\//,
                /^public\/applications\//
              ];
              const pathMatches = allowedPatterns.some(pattern => pattern.test(url));
              console.error('🚫 File path matches allowed patterns:', pathMatches);
              
              // Provide more specific guidance
              const suggestion = pathMatches 
                ? "The file path matches expected patterns but access is still denied. This may be an AWS IAM policy issue or the storage configuration needs to be redeployed."
                : "The file path doesn't match the configured storage access patterns.";
              
              alert(`Access denied for file: ${name}.\n\nFile path: "${url}"\nIssue: ${suggestion}\n\nPlease check the browser console for detailed debugging information.`);
            } else if (errorObj.code === 'NoSuchKey' || errorObj.statusCode === 404) {
              alert('File not found. The file may have been deleted or moved.');
            } else if (errorObj.code === 'TokenExpiredError') {
              alert('Your session has expired. Please log in again.');
            } else {
              alert(`Failed to download file: ${errorObj.message || 'Unknown error'}`);
            }
          }
  };

                const handleDownloadAllFiles = async () => {
          if (!selectedApp) return;

          // Ensure user is authenticated and has valid session
          if (!currentUser || !currentUser.isAuthenticated) {
            alert('User not authenticated. Please log in again.');
            return;
          }

          const allFiles = getAllFiles(selectedApp);
          if (allFiles.length === 0) {
            alert('No files available to download.');
            return;
          }

          try {
            const zip = new JSZip();
            const applicationId = selectedApp.applicationId || selectedApp.id || 'unknown';

            // Add each file to the ZIP
            for (const file of allFiles) {
              try {
                // Ensure we have a valid auth session before calling getUrl
                try {
                  await fetchAuthSession();
                } catch (authError) {
                  console.warn('⚠️ Auth session check failed, proceeding anyway:', authError);
                }

                // Get the download URL for the file
                const downloadUrl = await getUrl({
                  key: file.url,
                  options: {
                    validateObjectExistence: false,
                    expiresIn: 3600 // 1 hour
                  }
                });

                // Fetch the file content
                const response = await fetch(downloadUrl.url.toString());
                if (!response.ok) {
                  throw new Error(`Failed to fetch ${file.name}: ${response.statusText}`);
                }

                const fileBlob = await response.blob();
                const originalFilename = getOriginalFilename(file.url, file.name);

                // Add file to ZIP with original filename
                zip.file(originalFilename, fileBlob);

              } catch (error) {
                console.error(`Failed to add ${file.name} to ZIP:`, error);
                // Continue with other files even if one fails
              }
            }

            // Generate the ZIP file
            const zipBlob = await zip.generateAsync({ type: 'blob' });

            // Create download link for the ZIP file
            const link = document.createElement('a');
            link.href = URL.createObjectURL(zipBlob);
            link.download = `${applicationId}.zip`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the object URL
            URL.revokeObjectURL(link.href);

          } catch (error) {
            console.error('Error creating ZIP file:', error);
            alert('Failed to create ZIP file. Please try downloading files individually.');
          }
        };





  // Function to get filtered applications
  const getFilteredApplications = (): Application[] => {
    const filteredApps = (applications || [])
    .filter(app => {
      // Filter out applications with Award Name = "N/A" - only for judges
      if (isJudge() && (app.applicationDetail.awardName === 'N/A' || app.applicationDetail.awardName === null || app.applicationDetail.awardName === undefined || app.applicationDetail.awardName === '')) {
        return false;
      }

      // For judges, only show applications from their assigned categories
      if (isJudge()) {
        const assignedCategories = currentUser?.assignedCategories || [];
        if (assignedCategories.length > 0 && !assignedCategories.includes(app.applicationDetail.awardCategory)) {
          return false;
        }
      }

      const matchesCategory = filteredCategory === 'ALL' || app.applicationDetail.awardCategory === filteredCategory;
      const matchesSearch = !search.trim() ||
        app.applicationDetail.companyNameEng?.toLowerCase().includes(search.toLowerCase()) ||
        app.applicationDetail.companyNameChi?.toLowerCase().includes(search.toLowerCase()) ||
        app.applicationDetail.entryTitleEng?.toLowerCase().includes(search.toLowerCase()) ||
        app.applicationDetail.entryTitleChi?.toLowerCase().includes(search.toLowerCase()) ||
        app.applicationDetail.primaryContactName?.toLowerCase().includes(search.toLowerCase()) ||
        app.applicationDetail.primaryContactEmail?.toLowerCase().includes(search.toLowerCase()) ||
        app.id?.toLowerCase().includes(search.toLowerCase());
      return matchesCategory && matchesSearch;
    });

    const sortedApps = filteredApps.sort((a, b) => {
      let aValue: string | number | Date, bValue: string | number | Date;

      switch (sortBy) {
        case 'applicationId':
          aValue = a.applicationId || '';
          bValue = b.applicationId || '';
          break;
        case 'companyNameEng':
          aValue = a.applicationDetail.companyNameEng || '';
          bValue = b.applicationDetail.companyNameEng || '';
          break;
        case 'companyNameChi':
          aValue = a.applicationDetail.companyNameChi || '';
          bValue = b.applicationDetail.companyNameChi || '';
          break;
        case 'entryTitleEng':
          aValue = a.applicationDetail.entryTitleEng || '';
          bValue = b.applicationDetail.entryTitleEng || '';
          break;
        case 'awardCategory':
          aValue = getAwardCategoryLabel(a.applicationDetail.awardCategory) || '';
          bValue = getAwardCategoryLabel(b.applicationDetail.awardCategory) || '';
          break;
        case 'awardName':
          aValue = a.applicationDetail.awardName || '';
          bValue = b.applicationDetail.awardName || '';
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0);
          bValue = new Date(b.createdAt || 0);
          break;
        default:
          aValue = '';
          bValue = '';
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });


    return sortedApps;
  };

  const handleDownload = () => {
    try {
      const appsToExport = getFilteredApplications();
      if (appsToExport.length === 0) {
        alert('No applications to export.');
        return;
      }

      // Prepare data for Excel export
      const excelData = appsToExport.map((app: Application, index: number) => ({
        'No.': index + 1,
        'Application ID': app.applicationId || 'N/A',
        'Company Name (English)': app.applicationDetail.companyNameEng || 'N/A',
        'Company Name (Chinese)': app.applicationDetail.companyNameChi || 'N/A',
        'Entry Title (English)': app.applicationDetail.entryTitleEng || 'N/A',
                 'Entry Title (Chinese)': app.applicationDetail.entryTitleChi || 'N/A',
         'Entry Description': app.applicationDetail.entryDescription || 'N/A',
         'Award Category': getAwardCategoryLabel(app.applicationDetail.awardCategory) || 'N/A',
         'Award Name': app.applicationDetail.awardName || 'N/A',
         'Company Description': app.applicationDetail.companyDescription || 'N/A',
        'Business Registration No.': app.applicationDetail.businessRegNo || 'N/A',
        'Incorporation No.': app.applicationDetail.incorporationNo || 'N/A',
        'Incorporation Date': app.applicationDetail.incorporationDate || 'N/A',
        'Company Address': app.applicationDetail.companyAddress || 'N/A',
        'Company CEO': app.applicationDetail.companyCEO || 'N/A',
        'Company Business': app.applicationDetail.companyBusiness || 'N/A',
        'Company URL': app.applicationDetail.companyURL || 'N/A',
        'Is Startup': app.applicationDetail.isStartup || 'N/A',
        'Employee Count': app.applicationDetail.employeeCount || 'N/A',
        'Incubation Program': getIncubationProgramDisplay(app.applicationDetail.incubationProgram),
        'Incubation Description': app.applicationDetail.incubationDescription || 'N/A',
        'Primary Contact Name': app.applicationDetail.primaryContactName || 'N/A',
        'Primary Contact Title': app.applicationDetail.primaryContactTitle || 'N/A',
        'Primary Contact Phone': app.applicationDetail.primaryContactPhone || 'N/A',
        'Primary Contact Email': app.applicationDetail.primaryContactEmail || 'N/A',
        'Secondary Contact Name': app.applicationDetail.secondaryContactName || 'N/A',
        'Secondary Contact Title': app.applicationDetail.secondaryContactTitle || 'N/A',
        'Secondary Contact Phone': app.applicationDetail.secondaryContactPhone || 'N/A',
        'Secondary Contact Email': app.applicationDetail.secondaryContactEmail || 'N/A',
        'Innovation & Creativity': app.applicationDetail.innovationCreativity || 'N/A',
        'Functionality & Ergonomics': app.applicationDetail.functionalityErgonomics || 'N/A',
        'Quality': app.applicationDetail.quality || 'N/A',
        'Benefits & Impact': app.applicationDetail.benefitsImpact || 'N/A',
        'Market Potential': app.applicationDetail.marketPotential || 'N/A',
        'Ecological Compatibility': app.applicationDetail.ecologicalCompatibility || 'N/A',
        'Video Link': app.applicationDetail.videoLink || 'N/A',
        'Patent Status': app.applicationDetail.patentStatus || 'N/A',
        'Patent Number': app.applicationDetail.patentNumber || 'N/A',
        'Patent Title': app.applicationDetail.patentTitle || 'N/A',
        'Patent Proprietor': app.applicationDetail.patentProprietor || 'N/A',
        'Patent Country': app.applicationDetail.patentCountry || 'N/A',
        'Patent Date': app.applicationDetail.patentDate || 'N/A',
        'Intellectual Property': app.applicationDetail.intellectualProperty || 'N/A',
        'Dispute Description': app.applicationDetail.disputeDescription || 'N/A',
        'Submission Usage': app.applicationDetail.submissionUsage ? 'Yes' : 'No',
        'Application Declaration': app.applicationDetail.applicationDeclaration ? 'Yes' : 'No',
        'Event Admin Cost': app.applicationDetail.eventAdminCost ? 'Yes' : 'No',
        'Application Status': app.applicationStatus || 'N/A',
        'Record Status': app.status || 'N/A',
        'Created Date': app.createdAt ? new Date(app.createdAt).toLocaleDateString() : 'N/A',
        'Updated Date': app.updatedAt ? new Date(app.updatedAt).toLocaleDateString() : 'N/A',
                 'Updated By': app.updatedBy === 'system' ? 'Public Form Submission' : app.updatedBy || 'N/A',
        'Files Available': getAllFiles(app).length > 0 ? 'Yes' : 'No',
        'Number of Files': getAllFiles(app).length
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 5 },   // No.
        { wch: 15 },  // Application ID
        { wch: 30 },  // Company Name (English)
        { wch: 30 },  // Company Name (Chinese)
        { wch: 40 },  // Entry Title (English)
                 { wch: 40 },  // Entry Title (Chinese)
         { wch: 50 },  // Entry Description
         { wch: 35 },  // Award Category
         { wch: 30 },  // Award Name
         { wch: 50 },  // Company Description
        { wch: 20 },  // Business Registration No.
        { wch: 20 },  // Incorporation No.
        { wch: 15 },  // Incorporation Date
        { wch: 40 },  // Company Address
        { wch: 25 },  // Company CEO
        { wch: 30 },  // Company Business
        { wch: 30 },  // Company URL
        { wch: 10 },  // Is Startup
        { wch: 15 },  // Employee Count
        { wch: 20 },  // Incubation Program
        { wch: 40 },  // Incubation Description
        { wch: 25 },  // Primary Contact Name
        { wch: 25 },  // Primary Contact Title
        { wch: 20 },  // Primary Contact Phone
        { wch: 30 },  // Primary Contact Email
        { wch: 25 },  // Secondary Contact Name
        { wch: 25 },  // Secondary Contact Title
        { wch: 20 },  // Secondary Contact Phone
        { wch: 30 },  // Secondary Contact Email
        { wch: 25 },  // Innovation & Creativity
        { wch: 25 },  // Functionality & Ergonomics
        { wch: 15 },  // Quality
        { wch: 25 },  // Benefits & Impact
        { wch: 20 },  // Market Potential
        { wch: 25 },  // Ecological Compatibility
        { wch: 30 },  // Video Link
        { wch: 20 },  // Patent Status
        { wch: 20 },  // Patent Number
        { wch: 30 },  // Patent Title
        { wch: 25 },  // Patent Proprietor
        { wch: 15 },  // Patent Country
        { wch: 15 },  // Patent Date
        { wch: 25 },  // Intellectual Property
        { wch: 40 },  // Dispute Description
        { wch: 15 },  // Submission Usage
        { wch: 20 },  // Application Declaration
        { wch: 15 },  // Event Admin Cost
        { wch: 15 },  // Status
        { wch: 15 },  // Created Date
        { wch: 20 },  // Updated By
        { wch: 15 },  // Files Available
        { wch: 15 }   // Number of Files
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Applications');

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const categoryFilter = filteredCategory !== 'ALL' ? `_${filteredCategory}` : '';
      const filename = `applications_export${categoryFilter}_${timestamp}.xlsx`;

      // Save the file
      XLSX.writeFile(workbook, filename);

      // Show success message
      alert(`Successfully exported ${appsToExport.length} applications to ${filename}`);
    } catch (error) {
      alert('Failed to export applications. Please try again.');
    }
  };

  if (loading || userLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {userLoading ? 'Loading user information...' : 'Loading applications...'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please wait while we fetch your information from the database.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: { xs: 2, sm: 3 },
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        mb: 3,
        flexShrink: 0
      }}>
        <Typography variant="h4" sx={{
          fontSize: { xs: '1.5rem', sm: '2.125rem' }
        }}>
          {isJudge() ? 'Endorsement' : 'Applications'}
        </Typography>
        {!isJudge() && (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{
              minWidth: { xs: '100px', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              px: { xs: 1.5, sm: 2 }
            }}
          >
            Export
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}


      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 2 },
        mb: 3,
        flexShrink: 0,
        alignItems: { xs: 'stretch', sm: 'center' }
      }}>
        <TextField
          label="Search"
          value={search}
          onChange={handleSearchChange}
          size="small"
          sx={{
            minWidth: { xs: '100%', sm: 200 },
            maxWidth: { xs: '100%', sm: 300 }
          }}
        />
        <FormControl
          size="small"
          sx={{
            minWidth: { xs: '100%', sm: 200 },
            maxWidth: { xs: '100%', sm: 300 }
          }}
        >
          <InputLabel>Category</InputLabel>
          <Select
            value={filteredCategory}
            onChange={handleFilterChange}
            label="Category"
            sx={{
              '& .MuiSelect-select': {
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                lineHeight: 1.4
              }
            }}
          >
            <MenuItem value="ALL">
              {isJudge() ? 'All Assigned Categories' : 'All Categories'}
            </MenuItem>
            {(() => {
              const availableCategories = getAvailableCategories();
              // console.log('Rendering dropdown - currentUser:', currentUser);
              // console.log('Rendering dropdown - isJudge():', isJudge());
              // console.log('Rendering dropdown with categories:', availableCategories);
              return availableCategories.map((category) => (
                <MenuItem key={category.value} value={category.value} sx={{
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  lineHeight: 1.4,
                  py: 1
                }}>
                  {getAwardCategoryLabel(category.value)}
                </MenuItem>
              ));
            })()}
          </Select>
        </FormControl>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {getFilteredApplications().length} applications
          </Typography>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          minHeight: 0,
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          '& .MuiTable-root': {
            backgroundColor: '#ffffff',
          },
          '& .MuiTableHead-root': {
            backgroundColor: '#f1f5f9',
          },
          '& .MuiTableRow-root:nth-of-type(odd)': {
            backgroundColor: '#f8fafc',
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: '#e0f2fe',
          },
          '& .MuiTableCell-root': {
            borderBottom: '1px solid #e2e8f0',
            color: '#1e293b',
          },
          '& .MuiTableCell-head': {
            backgroundColor: '#f1f5f9',
            color: '#1e293b',
            fontWeight: 600,
          },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                minWidth: { xs: 60, sm: 80 },
                width: { xs: '60px', sm: '80px' }
              }}>
                Details
              </TableCell>
              <TableCell sx={{
                minWidth: { xs: 120, sm: 150 },
                display: { xs: 'none', md: 'table-cell' }
              }}>
                <TableSortLabel
                  active={sortBy === 'applicationId'}
                  direction={sortBy === 'applicationId' ? sortOrder : 'asc'}
                  onClick={() => handleSort('applicationId')}
                >
                  Application ID
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{
                minWidth: { xs: 150, sm: 200 },
                maxWidth: { xs: 150, sm: 200 }
              }}>
                <TableSortLabel
                  active={sortBy === 'companyNameEng'}
                  direction={sortBy === 'companyNameEng' ? sortOrder : 'asc'}
                  onClick={() => handleSort('companyNameEng')}
                >
                  Company Name (Eng)
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{
                minWidth: { xs: 150, sm: 200 },
                display: { xs: 'none', lg: 'table-cell' }
              }}>
                <TableSortLabel
                  active={sortBy === 'companyNameChi'}
                  direction={sortBy === 'companyNameChi' ? sortOrder : 'asc'}
                  onClick={() => handleSort('companyNameChi')}
                >
                  Company Name (Chi)
                </TableSortLabel>
              </TableCell>
              {/* Show Entry Title only for non-judges */}
              {!isJudge() && (
                <TableCell sx={{
                  minWidth: { xs: 120, sm: 150 },
                  display: { xs: 'none', lg: 'table-cell' }
                }}>
                  <TableSortLabel
                    active={sortBy === 'entryTitleEng'}
                    direction={sortBy === 'entryTitleEng' ? sortOrder : 'asc'}
                    onClick={() => handleSort('entryTitleEng')}
                  >
                    Entry Title (Eng)
                  </TableSortLabel>
                </TableCell>
              )}
              {/* Show Award Category only for non-judges */}
              {!isJudge() && (
                <TableCell sx={{
                  minWidth: { xs: 150, sm: 200 },
                  display: { xs: 'none', sm: 'table-cell' }
                }}>
                  <TableSortLabel
                    active={sortBy === 'awardCategory'}
                    direction={sortBy === 'awardCategory' ? sortOrder : 'asc'}
                    onClick={() => handleSort('awardCategory')}
                  >
                    Award Category
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell sx={{
                minWidth: { xs: 120, sm: 150 },
                display: { xs: 'none', md: 'table-cell' }
              }}>
                <TableSortLabel
                  active={sortBy === 'awardName'}
                  direction={sortBy === 'awardName' ? sortOrder : 'asc'}
                  onClick={() => handleSort('awardName')}
                >
                  Award Name
                </TableSortLabel>
              </TableCell>
              {/* Show Created At only for non-judges */}
              {!isJudge() && (
                <TableCell sx={{
                  minWidth: { xs: 120, sm: 150 },
                  display: { xs: 'none', md: 'table-cell' }
                }}>
                  <TableSortLabel
                    active={sortBy === 'createdAt'}
                    direction={sortBy === 'createdAt' ? sortOrder : 'asc'}
                    onClick={() => handleSort('createdAt')}
                  >
                    Created At
                  </TableSortLabel>
                </TableCell>
              )}
              {canEndorseApplications() && (
                <TableCell sx={{
                  minWidth: { xs: 120, sm: 150 }
                }}>
                  Endorsement
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              const appsToShow = getFilteredApplications();
              return appsToShow.length > 0 ? (
                appsToShow.map((app) => (
                <TableRow key={app.id || `app-${Math.random()}`} hover>
                  <TableCell>
                    <IconButton
                      onClick={() => handleInfoClick(app)}
                      size="small"
                      color="primary"
                    >
                      <InfoIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {app.applicationId || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {app.applicationDetail.companyNameEng || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Typography variant="body2" noWrap>
                      {app.applicationDetail.companyNameChi || 'N/A'}
                    </Typography>
                  </TableCell>
                  {/* Show Entry Title only for non-judges */}
                  {!isJudge() && (
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Typography variant="body2" noWrap>
                        {app.applicationDetail.entryTitleEng || 'N/A'}
                      </Typography>
                    </TableCell>
                  )}
                  {/* Show Award Category only for non-judges */}
                  {!isJudge() && (
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2" noWrap>
                        {getAwardCategoryLabel(app.applicationDetail.awardCategory) || 'N/A'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2" noWrap>
                      {app.applicationDetail.awardName || 'N/A'}
                    </Typography>
                  </TableCell>
                  {/* Show Created At only for non-judges */}
                  {!isJudge() && (
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2">
                        {app.createdAt ? new Date(app.createdAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                  )}
                  {canEndorseApplications() && (
                    <TableCell>
                      {app.id && userEndorsedAppIds.has(app.id) ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Submitted"
                          color="success"
                          variant="filled"
                          size="small"
                          onClick={() => handleViewSubmittedInfo(app)}
                          sx={{ cursor: 'pointer' }}
                        />
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleEndorseClick(app)}
                          sx={{ textTransform: 'none' }}
                        >
                          ENDORSE
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
                ))
              ) : (
              <TableRow>
                <TableCell colSpan={isJudge() ? (canEndorseApplications() ? 5 : 4) : (canEndorseApplications() ? 9 : 8)} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No applications found
                  </Typography>
                </TableCell>
              </TableRow>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>

      {(() => {
        const appsToShow = getFilteredApplications();
        return appsToShow.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            No applications found
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {applications.length === 0
              ? 'No applications have been submitted yet.'
              : 'No applications match your current search criteria.'}
          </Typography>
          {applications.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Try refreshing the page or check if the database connection is working.
            </Typography>
          )}
        </Box>
        );
      })()}

      <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h5" sx={{ mb: 1 }}>
                Application Details
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Application ID: {selectedApp?.applicationId || 'N/A'} | Database ID: {selectedApp?.id}
              </Typography>
            </Box>
            {!isEditMode && canEditApplications() ? (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditClick}
                sx={{ ml: 2 }}
              >
                Edit
              </Button>
            ) : isEditMode ? (
              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveEdit}
                  disabled={saving}
                  color="primary"
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </Box>
            ) : null}
      </Box>
        </DialogTitle>
        <DialogContent>
          {selectedApp && (
            <Box sx={{ mt: 2 }}>
              {/* Basic Information */}
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                Basic Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Company Name (English)</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editData?.applicationDetail?.companyNameEng || ''}
                      onChange={(e) => handleEditFieldChange('companyNameEng', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1">{selectedApp.applicationDetail?.companyNameEng || 'N/A'}</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Company Name (Chinese)</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editData?.applicationDetail?.companyNameChi || ''}
                      onChange={(e) => handleEditFieldChange('companyNameChi', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1">{selectedApp.applicationDetail?.companyNameChi || 'N/A'}</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Entry Title (English)</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editData?.applicationDetail?.entryTitleEng || ''}
                      onChange={(e) => handleEditFieldChange('entryTitleEng', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1">{selectedApp.applicationDetail?.entryTitleEng || 'N/A'}</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Entry Title (Chinese)</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editData?.applicationDetail?.entryTitleChi || ''}
                      onChange={(e) => handleEditFieldChange('entryTitleChi', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1">{selectedApp.applicationDetail?.entryTitleChi || 'N/A'}</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Entry Description</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      size="small"
                      value={editData?.applicationDetail?.entryDescription || ''}
                      onChange={(e) => handleEditFieldChange('entryDescription', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedApp.applicationDetail?.entryDescription || 'N/A'}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Award Category</Typography>
                  {isEditMode ? (
                    <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                      <InputLabel id="award-category-label">Select Award Category</InputLabel>
                      <Select
                        key={`award-category-${editData?.applicationDetail?.awardCategory || 'empty'}`}
                        labelId="award-category-label"
                        value={editData?.applicationDetail?.awardCategory || ''}
                        onChange={(e) => {
                          // console.log('Select onChange triggered:', e.target.value);
                          handleEditFieldChange('awardCategory', e.target.value);
                        }}
                        displayEmpty
                        label="Select Award Category"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300
                            }
                          }
                        }}
                        onOpen={() => {}}
                        onClose={() => {}}
                      >
                        <MenuItem value="">
                          <em>Select an award category</em>
                        </MenuItem>
                        {AWARD_CATEGORIES.map((category) => (
                          <MenuItem key={category.value} value={category.value}>
                            {category.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">{getAwardCategoryLabel(selectedApp.applicationDetail?.awardCategory) || 'N/A'}</Typography>
                  )}
                </Box>
                                 <Box>
                   <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Award Name</Typography>
                   {isEditMode ? (
                     <TextField
                       fullWidth
                       size="small"
                       value={editData?.applicationDetail?.awardName || ''}
                       onChange={(e) => handleEditFieldChange('awardName', e.target.value)}
                       placeholder="Enter award name"
                       sx={{ mt: 0.5 }}
                     />
                   ) : (
                     <Typography variant="body1">{selectedApp.applicationDetail?.awardName || 'N/A'}</Typography>
                   )}
                 </Box>
                 <Box>
                   <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Business Registration No.</Typography>
                   {isEditMode ? (
                     <TextField
                       fullWidth
                       size="small"
                       value={editData?.applicationDetail?.businessRegNo || ''}
                       onChange={(e) => handleEditFieldChange('businessRegNo', e.target.value)}
                       sx={{ mt: 0.5 }}
                     />
                   ) : (
                     <Typography variant="body1">{selectedApp.applicationDetail?.businessRegNo || 'N/A'}</Typography>
                   )}
                 </Box>
              </Box>

              {/* Contact Information */}
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                Contact Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Primary Contact</Typography>
                  {isEditMode ? (
                    <Box sx={{ mt: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Name"
                        value={editData?.applicationDetail?.primaryContactName || ''}
                        onChange={(e) => handleEditFieldChange('primaryContactName', e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Title"
                        value={editData?.applicationDetail?.primaryContactTitle || ''}
                        onChange={(e) => handleEditFieldChange('primaryContactTitle', e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Email"
                        value={editData?.applicationDetail?.primaryContactEmail || ''}
                        onChange={(e) => handleEditFieldChange('primaryContactEmail', e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Phone"
                        value={editData?.applicationDetail?.primaryContactPhone || ''}
                        onChange={(e) => handleEditFieldChange('primaryContactPhone', e.target.value)}
                      />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="body1">{selectedApp.applicationDetail?.primaryContactName || 'N/A'}</Typography>
                      <Typography variant="body2">{selectedApp.applicationDetail?.primaryContactTitle || ''}</Typography>
                      <Typography variant="body2">{selectedApp.applicationDetail?.primaryContactEmail || 'N/A'}</Typography>
                      <Typography variant="body2">{selectedApp.applicationDetail?.primaryContactPhone || 'N/A'}</Typography>
                    </>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Secondary Contact</Typography>
                  {isEditMode ? (
                    <Box sx={{ mt: 0.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Name"
                        value={editData?.applicationDetail?.secondaryContactName || ''}
                        onChange={(e) => handleEditFieldChange('secondaryContactName', e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Title"
                        value={editData?.applicationDetail?.secondaryContactTitle || ''}
                        onChange={(e) => handleEditFieldChange('secondaryContactTitle', e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Email"
                        value={editData?.applicationDetail?.secondaryContactEmail || ''}
                        onChange={(e) => handleEditFieldChange('secondaryContactEmail', e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Phone"
                        value={editData?.applicationDetail?.secondaryContactPhone || ''}
                        onChange={(e) => handleEditFieldChange('secondaryContactPhone', e.target.value)}
                      />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="body1">{selectedApp.applicationDetail?.secondaryContactName || 'N/A'}</Typography>
                      <Typography variant="body2">{selectedApp.applicationDetail?.secondaryContactTitle || ''}</Typography>
                      <Typography variant="body2">{selectedApp.applicationDetail?.secondaryContactEmail || 'N/A'}</Typography>
                      <Typography variant="body2">{selectedApp.applicationDetail?.secondaryContactPhone || 'N/A'}</Typography>
                    </>
                  )}
                </Box>
              </Box>

              {/* Company Details */}
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                Company Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Company Description</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      size="small"
                      value={editData?.applicationDetail?.companyDescription || ''}
                      onChange={(e) => handleEditFieldChange('companyDescription', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedApp.applicationDetail.companyDescription || 'N/A'}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Business Type</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editData?.applicationDetail?.companyBusiness || ''}
                      onChange={(e) => handleEditFieldChange('companyBusiness', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1">{selectedApp.applicationDetail.companyBusiness || 'N/A'}</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Company URL</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editData?.applicationDetail?.companyURL || ''}
                      onChange={(e) => handleEditFieldChange('companyURL', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {selectedApp.applicationDetail.companyURL ? (
                        <a href={selectedApp.applicationDetail.companyURL} target="_blank" rel="noopener noreferrer">
                          {selectedApp.applicationDetail.companyURL}
                        </a>
                      ) : 'N/A'}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">CEO</Typography>
                  <Typography variant="body1">{selectedApp?.applicationDetail?.companyCEO || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Is Startup</Typography>
                  <Typography variant="body1">{selectedApp?.applicationDetail?.isStartup || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Employee Count</Typography>
                  <Typography variant="body1">{selectedApp?.applicationDetail?.employeeCount || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Incubation Programme</Typography>
                  {isEditMode ? (
                    <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                      <InputLabel id="incubation-program-label">Select Incubation Programme</InputLabel>
                      <Select
                        labelId="incubation-program-label"
                        value={editData?.applicationDetail?.incubationProgram || ''}
                        onChange={(e) => handleEditFieldChange('incubationProgram', e.target.value)}
                        displayEmpty
                        label="Select Incubation Programme"
                      >
                        <MenuItem value="">
                          <em>Select an incubation programme</em>
                        </MenuItem>
                        <MenuItem value="N">Not Applicable 不適用</MenuItem>
                        <MenuItem value="C">Cyberport 數碼港</MenuItem>
                        <MenuItem value="H">Hong Kong Science & Technology Parks Corporation (HKSTP) 香港科技園公司</MenuItem>
                        <MenuItem value="O">Others 其他</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">{getIncubationProgramDisplay(selectedApp?.applicationDetail?.incubationProgram)}</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Incubation Description</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      value={editData?.applicationDetail?.incubationDescription || ''}
                      onChange={(e) => handleEditFieldChange('incubationDescription', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedApp?.applicationDetail?.incubationDescription || 'N/A'}
                    </Typography>
                  )}
                </Box>
              </Box>



              {/* Judging Materials */}
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                Judging Materials
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Innovation & Creativity</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      value={editData?.applicationDetail?.innovationCreativity || ''}
                      onChange={(e) => handleEditFieldChange('innovationCreativity', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedApp?.applicationDetail?.innovationCreativity || 'N/A'}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Functionality & Ergonomics</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      value={editData?.applicationDetail?.functionalityErgonomics || ''}
                      onChange={(e) => handleEditFieldChange('functionalityErgonomics', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedApp?.applicationDetail?.functionalityErgonomics || 'N/A'}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Quality</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      value={editData?.applicationDetail?.quality || ''}
                      onChange={(e) => handleEditFieldChange('quality', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedApp?.applicationDetail?.quality || 'N/A'}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Benefits & Impact</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      value={editData?.applicationDetail?.benefitsImpact || ''}
                      onChange={(e) => handleEditFieldChange('benefitsImpact', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedApp?.applicationDetail?.benefitsImpact || 'N/A'}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Market Potential</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      value={editData?.applicationDetail?.marketPotential || ''}
                      onChange={(e) => handleEditFieldChange('marketPotential', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedApp?.applicationDetail?.marketPotential || 'N/A'}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Ecological Compatibility</Typography>
                  {isEditMode ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      value={editData?.applicationDetail?.ecologicalCompatibility || ''}
                      onChange={(e) => handleEditFieldChange('ecologicalCompatibility', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedApp?.applicationDetail?.ecologicalCompatibility || 'N/A'}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Files and Links */}
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                Files and Links
              </Typography>

              {/* Video Link */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Video Link
                </Typography>
                {isEditMode ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editData?.applicationDetail?.videoLink || ''}
                    onChange={(e) => handleEditFieldChange('videoLink', e.target.value)}
                    placeholder="Enter video URL"
                    sx={{ mt: 0.5 }}
                  />
                ) : (
                  selectedApp?.applicationDetail?.videoLink ? (
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      href={selectedApp.applicationDetail.videoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: 'none' }}
                    >
                      Watch Video
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">N/A</Typography>
                  )
                )}
              </Box>

              {/* All Files */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    All Files
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadAllFiles}
                    size="small"
                    sx={{ textTransform: 'none' }}
                  >
                    Download All Files
                  </Button>
                </Box>
                                  {(() => {
                    const allFiles = selectedApp ? getAllFiles(selectedApp) : [];
                    if (allFiles.length === 0) {
                    return <Typography variant="body2" color="text.secondary">No files uploaded</Typography>;
                  }
                  return (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>

                      {allFiles.map((file, index) => (

                        <Card variant="outlined" key={index}>
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              {getFileIcon(file.type)}
                              <Typography variant="body2" sx={{ ml: 1, fontWeight: 'medium' }}>
                                {file.name}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label={file.type.toUpperCase()}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {file.type === 'image' && (
                                <Button
                                  size="small"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => handleFilePreview(file.url, file.name)}
                                  sx={{ minWidth: 'auto', px: 1 }}
                                >
                                  Preview
                                </Button>
                              )}
                                                             <Button
                                 size="small"
                                 startIcon={<DownloadIcon />}
                                 onClick={(e) => {
                                   e.preventDefault();
                                   e.stopPropagation();
                                   handleFileDownload(file.url, file.name);
                                 }}
                                 sx={{ minWidth: 'auto', px: 1 }}
                               >
                                 Download
                               </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  );
                })()}
              </Box>

              {/* Metadata */}
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                Metadata
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Typography variant="body1">{selectedApp.status || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                  <Typography variant="body1">
                    {selectedApp.createdAt ? new Date(selectedApp.createdAt).toLocaleString() : 'N/A'}
                  </Typography>
                </Box>
                                 <Box>
                   <Typography variant="subtitle2" color="text.secondary">Last Updated By</Typography>
                   <Typography variant="body1">
                     {selectedApp.updatedBy === 'system' ? 'Public Form Submission' : selectedApp.updatedBy || 'N/A'}
                   </Typography>
                 </Box>
              </Box>

              {/* Mark of Judges Panel - Only for Super Admin and Admin */}
              {!isJudge() && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    Mark of Judges Panel
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                    {(() => {
                      const appEndorsements = selectedApp.id ? adminEndorsements.get(selectedApp.id) || [] : [];
                      return appEndorsements.length > 0 ? (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}>Judge</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}>Mark</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}>Comment</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {appEndorsements.map((endorsement, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    {endorsement.judgeName || endorsement.judgeEmail || 'Unknown Judge'}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                      {endorsement.mark}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {endorsement.comment || 'No comment provided'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                          No endorsements submitted yet
                        </Typography>
                      );
                    })()}
                  </Paper>
                </Box>
              )}

            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* File Preview Modal */}
      <Dialog
        open={filePreviewOpen}
        onClose={() => setFilePreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            {previewFile?.name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {previewFile && (
            <Box sx={{ textAlign: 'center' }}>
              {previewFile.type === 'image' ? (
                <img
                  src={previewFile.url}
                  alt={previewFile.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain'
                  }}
                />
              ) : previewFile.type === 'pdf' ? (
                <iframe
                  src={previewFile.url}
                  width="100%"
                  height="70vh"
                  title={previewFile.name}
                  style={{ border: 'none' }}
                />
              ) : (
                <Box sx={{ p: 4 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    File Preview Not Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    This file type cannot be previewed in the browser.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      handleFileDownload(previewFile.url, previewFile.name);
                      setFilePreviewOpen(false);
                    }}
                  >
                    Download File
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
                 <DialogActions>
           <Button onClick={() => setFilePreviewOpen(false)}>Close</Button>
         </DialogActions>
      </Dialog>

      {/* Endorsement Modal */}
      <Dialog open={endorsementModalOpen} onClose={handleEndorsementClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5">
            Endorsement for the Award Name
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Mark</InputLabel>
              <Select
                value={endorsementForm.mark}
                onChange={(e) => setEndorsementForm({ ...endorsementForm, mark: e.target.value })}
                label="Mark"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((mark) => (
                  <MenuItem key={mark} value={mark.toString()}>
                    {mark}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Your Comment"
              value={endorsementForm.comment}
              onChange={(e) => setEndorsementForm({ ...endorsementForm, comment: e.target.value })}
              inputProps={{ maxLength: 600 }}
              helperText={`${endorsementForm.comment.length}/600`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleEndorsementClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEndorsementSubmit}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Are you sure?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1">
            You will not be able to revise your choice.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmEndorsement}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submitted Information Modal */}
      <Dialog open={submittedInfoModalOpen} onClose={handleSubmittedInfoClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Submitted information
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedSubmittedEndorsement && (
            <Box>
              {/* Mark Display */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Mark: {selectedSubmittedEndorsement.mark}
                </Typography>
              </Box>

              {/* Comment Display */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Your Comment
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 2,
                    minHeight: 100,
                    backgroundColor: '#fafafa',
                    position: 'relative'
                  }}
                >
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedSubmittedEndorsement.comment || 'No comment provided'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      color: 'text.secondary'
                    }}
                  >
                    {selectedSubmittedEndorsement.comment?.length || 0} / 600
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSubmittedInfoClose}
            variant="contained"
            color="primary"
          >
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Applications;